import { useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiTab } from "@miplatform/components";
import { MAX_PAGE_SIZE } from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { IVerticalTabProps } from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { AutoTriggerFlow } from "./components/AutoTriggerFlow";
import {
  CreateAutoTriggerFormValues,
  CreateAutoTriggerModal,
} from "./components/CreateAutoTriggerModal";
import { EmptyAutoTriggerList } from "./components/EmptyAutoTriggerList";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import {
  AutoTriggerView,
  DEFAULT_AUTO_TRIGGER_FLOW,
} from "./autoTrigger.constants";
import { useOrderAutoTriggerViewModel } from "./useOrderAutoTriggerViewModel";

export const OrderAutoTrigger = () => {
  const { classes } = useStyles();
  const {
    isLoading,
    accounts,
    autoTriggerFlows,
    orderTemplates,
    activeTab,
    setActiveTab,
    getAccounts,
    getAutoTriggerFlows,
    getOrderTemplates,
    createAutoTriggerFlow,
    updateAutoTriggerFlow,
    deleteAutoTriggerFlow,
    updateAutoTriggerBuilder,
  } = useOrderAutoTriggerViewModel();
  const [isAddFlowModalOpen, handleAddFlowModalOpen, handleAddFlowModalClose] =
    useBoolean();
  const [activeVerticalTab, setActiveVerticalTab] = useState<
    string | number | null
  >(null);
  const [activeView, setActiveView] = useState(AutoTriggerView.List);
  const templatesToFlowMap = useMemo(() => {
    const filteredFlows = autoTriggerFlows.filter(
      (flow) => flow.id !== activeTab
    );
    const templatesToFlowMap: Record<string, string> = {};
    filteredFlows.forEach(({ name, dependencyMaps }) =>
      dependencyMaps.forEach(({ dependentId, principalId }) => {
        templatesToFlowMap[dependentId] = name as string;
        templatesToFlowMap[principalId] = name as string;
      })
    );

    return templatesToFlowMap;
  }, [activeTab, autoTriggerFlows]);
  const horizontalTabs = useMemo(
    () =>
      autoTriggerFlows
        .sort(
          (a, b) =>
            new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
        )
        .map((flow) => ({
          label: flow.name as string,
          value: flow.id,
        })),
    [autoTriggerFlows]
  );
  const verticalTabs = useMemo(
    () => [
      { label: DEFAULT_AUTO_TRIGGER_FLOW, value: DEFAULT_AUTO_TRIGGER_FLOW },
      ...accounts.map(({ id, name }) => ({ label: name, value: id })),
    ],
    [accounts]
  );
  const activeFlow = useMemo(
    () => autoTriggerFlows.find((flow) => flow.id === activeTab),
    [activeTab, autoTriggerFlows]
  );
  const disabledBuilders = useMemo(
    () =>
      activeFlow?.autoTriggerFlowDisableBuilderMaps.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>) || {},
    [activeFlow]
  );
  const buildersMap = useMemo(
    () =>
      accounts?.reduce((acc, { id, name }) => {
        acc[id] = name;
        return acc;
      }, {} as Record<string, string>) || {},
    [accounts]
  );

  const handleCreateAutoTriggerFlow = (data: CreateAutoTriggerFormValues) => {
    createAutoTriggerFlow(data, handleAddFlowModalClose);
  };

  const renderVerticalTabLabel = (tab: IVerticalTabProps<FieldValues>) => {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        {tab.label}
        {disabledBuilders[tab.value as string] && (
          <Box className={classes.disabledChip}>Disabled</Box>
        )}
      </Box>
    );
  };

  const renderActiveTab = () => {
    if (!activeFlow) return null;

    return (
      <AutoTriggerFlow
        key={activeFlow.id}
        flow={activeFlow}
        builderId={
          activeVerticalTab !== DEFAULT_AUTO_TRIGGER_FLOW
            ? String(activeVerticalTab)
            : undefined
        }
        isLoading={isLoading}
        orderTemplates={orderTemplates}
        disabledBuilders={disabledBuilders}
        activeView={activeView}
        templatesToFlowMap={templatesToFlowMap}
        buildersMap={buildersMap}
        onViewChange={setActiveView}
        onDelete={deleteAutoTriggerFlow}
        onSubmit={updateAutoTriggerFlow}
        onBuilderUpdate={updateAutoTriggerBuilder}
      />
    );
  };

  useEffect(() => {
    getAccounts();
    getAutoTriggerFlows();
    getOrderTemplates({ top: MAX_PAGE_SIZE });
  }, []);

  useEffect(() => {
    if (!activeTab || autoTriggerFlows.every((flow) => flow.id !== activeTab)) {
      setActiveTab(autoTriggerFlows[0]?.id);
      setActiveVerticalTab(verticalTabs[0].value);
    }
  }, [autoTriggerFlows, activeTab]);

  useEffect(() => {
    setActiveVerticalTab(verticalTabs[0].value);
  }, [activeTab]);

  return (
    <ConfigPageLayout
      hideOnBack
      loading={isLoading}
      title="Auto-Trigger"
      titleActionComponent={
        <AddButton onClick={handleAddFlowModalOpen} label="New Flow" />
      }
    >
      {autoTriggerFlows.length > 0 && (
        <MiTab
          tabs={horizontalTabs}
          scrollButtons="none"
          currentTab={activeTab}
          onTabChange={setActiveTab}
          borderBottom
        />
      )}
      {autoTriggerFlows.length === 0 ? (
        <Box padding={3} height="100%" width="100%">
          <EmptyAutoTriggerList />
        </Box>
      ) : (
        <VerticalTabsLayout
          hideStatusIcons
          hideNavigationButtons
          tabs={verticalTabs}
          value={activeVerticalTab}
          setValue={setActiveVerticalTab}
          onTabChange={(tab) => {
            setActiveVerticalTab(tab.value);
            setActiveView(AutoTriggerView.List);
          }}
          renderLabel={renderVerticalTabLabel}
          transparentBackground
        >
          <Box className={classes.root}>{activeTab && renderActiveTab()}</Box>
        </VerticalTabsLayout>
      )}
      <CreateAutoTriggerModal
        isOpen={isAddFlowModalOpen}
        isLoading={isLoading}
        onClose={handleAddFlowModalClose}
        onSubmit={handleCreateAutoTriggerFlow}
      />
    </ConfigPageLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: "100%",
    gap: theme.spacing(2),
  },
  headerWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  header: {
    backgroundColor: theme.palette.primary.medium,
    padding: theme.spacing(0, 3),
    borderRadius: 4,
    minHeight: 42,
    display: "flex",
    alignItems: "center",
  },
  contentWrapper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 3, 3),
    borderRadius: "0 0 4px 4px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  orderCard: {
    display: "flex",
    backgroundColor: theme.palette.purple.light,
    border: `1px solid #DAC2FF`,
    borderRadius: theme.spacing(1),
    color: theme.palette.deepPurple.medium,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.small,
    padding: theme.spacing(5, 3),
    boxShadow: "0px 2px 2px 0px #0000001F",
    spacing: 2,
  },
  disabledChip: {
    backgroundColor: theme.palette.grey.accent,
    border: `1px solid ${theme.palette.grey.darkerAccent}`,
    padding: theme.spacing(0.5, 1),
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.xsmall,
  },
}));
